// src/components/appeals/AppealsColumns.tsx

import { get, includes, isEmpty } from 'lodash';
import { ActionsMenuItem, ActionsMenu, Button, Icon, Tooltip } from '@repo/ui';
import { DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { cn, dateUtil, numberUtil } from '@xtnd/web/shared/utils';
import { GroupByOption, InvoiceState, AppealStatus, canEdit, rollbackTargetsFor } from '@repo/types';

import { Appeal, AppealGroup } from '../../types';
import AppealStatusBadge from './AppealStatusBadge';
import { DAYS_PAST_DUE } from '../../constants';
import { QueryStringParams } from '@xtnd/web/shared/types';
import BARColumn from '../common/BARColumn';
import { AppealCheckboxCell, AppealCheckboxHeader } from './AppealCheckbox';

// Shared constants for Invoice Status rendering
export const INVOICE_STATUS_LABELS = {
  [InvoiceState.DRAFT]: '-',
  [InvoiceState.AWAITING_APPROVAL]: '-',
  [InvoiceState.AWAITING_PAYMENT]: 'Unpaid',
  [InvoiceState.PAID]: 'Paid',
  [InvoiceState.VOID]: 'Void',
  [InvoiceState.DELETED]: 'Deleted',
};
export const INVOICE_STATUS_CLASSES = {
  [InvoiceState.AWAITING_PAYMENT]: 'text-[#804F00] border-[#F4E69D] bg-[#FBF3C8]',
  [InvoiceState.PAID]: 'bg-[#B6F0E3] text-[#006968] border-[#80D9CB]',
  [InvoiceState.VOID]: 'bg-[#E9ECF0] text-[#3C4E62] border-[#D1D9E0]',
  [InvoiceState.DELETED]: 'bg-[#E9ECF0] text-[#3C4E62] border-[#D1D9E0]',
};
const HIDE_ATTORNEY_RESPONSE_STATUS = [AppealStatus.PRE_APPEAL];

/**
 * @description Generates the column definitions for the Appeals DataTable based on the grouping state.
 * @param {GroupByOption} groupBy - The current grouping selection.
 * @param {AppealGroup | null} rowSelected - The selected group for detail view.
 * @param {any} tableParams - Current table parameters.
 * @param {(invoiceId: string, isPending: boolean) => void} onMutationStateChange - Callback for row mutation state.
 * @param {(row: any) => string} getRowClassName - Function to get the CSS class for a row.
 * @returns {DataTableColumn<any>[]} - Array of column definitions.
 */

export interface AppealsColumnsProps {
  actionSelectedAppeal: Appeal | null;
  actionMenuRef: React.RefObject<HTMLDivElement>;
  actionRef: HTMLElement | null;
  groupBy: GroupByOption;
  rowSelected: AppealGroup | null;
  tableParams: QueryStringParams;
  isAppealSelected: boolean;
  appealSelectedParams: QueryStringParams;
  setIsActivityOpen: (isOpen: boolean) => void;
  setSelectedActivityAppeal: (appeal: Appeal) => void;
  setActionRef: (ref: HTMLElement | null) => void;
  setActionSelectedAppeal: (appeal: Appeal | null) => void;
  onMutationStateChange: (invoiceId: string, isPending: boolean) => void;
  handleCopyAttorneyResponseLink: (appeal: Appeal) => Promise<void>;
  handleViewAttorneyResponse: (appeal: Appeal) => void;
  handleRollback: (appeal: Appeal) => void;
  handleEdit?: (appeal: Appeal) => void;
  handleWriteOffRemaining?: (appeal: Appeal) => void;
  mutatingRows: Set<string>;
  currentPageInvoiceIds?: string[];
  clerkUserMap: Map<string, string>;
}
export function generateAppealsColumns({
  actionSelectedAppeal,
  actionMenuRef,
  actionRef,
  groupBy,
  rowSelected,
  tableParams,
  isAppealSelected,
  appealSelectedParams,
  setIsActivityOpen,
  setSelectedActivityAppeal,
  setActionRef,
  setActionSelectedAppeal,
  onMutationStateChange,
  handleCopyAttorneyResponseLink,
  handleViewAttorneyResponse,
  handleRollback,
  handleEdit,
  handleWriteOffRemaining,
  mutatingRows,
  currentPageInvoiceIds = [],
  clerkUserMap,
}: AppealsColumnsProps): DataTableColumn<any>[] {
  // --- INDIVIDUAL INVOICE COLUMNS (Ungrouped or Client Detail View) ---
  if (groupBy === GroupByOption.NONE || !isEmpty(rowSelected)) {
    const clientNameColumn = {
      key: 'clientName',
      header: 'Client',
      align: 'left',
      isResizable: true,
      defaultWidth: 200,
      renderCell: (_appeal: Appeal) => (
        <Tooltip content={_appeal.clientName} position="auto" className="rounded-md">
          <span className="text-sm text-gray-900 dark:text-gray-100 block">
            {_appeal.clientName}
          </span>
        </Tooltip>
      ),
    };

    const checkboxColumn: DataTableColumn<Appeal> = {
      key: 'select',
      header: <AppealCheckboxHeader currentPageInvoiceIds={currentPageInvoiceIds} />,
      align: 'center',
      renderCell: (row: Appeal) => <AppealCheckboxCell invoiceId={row.invoiceId} />,
    };

    const baseColumns = [
      {
        key: 'matterNumber',
        header: 'Matter',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: (_appeal: Appeal) => (
          <Tooltip
            content={_appeal.matterDisplayNumber ?? ''}
            position="auto"
            className="rounded-md"
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 block">
              {_appeal.matterDisplayNumber}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'assignTo',
        header: 'Assigned',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        className: 'min-w-[8rem]',
        renderCell: (_appeal: Appeal) => {
          const clerkEmail = _appeal.assignedTo;
          if (!clerkEmail) {
            return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
          }
          const clerkName = clerkUserMap.get(clerkEmail) ?? clerkEmail;
          return (
            <Tooltip content={clerkName} position="auto" className="rounded-md">
              <span className="inline-block px-2 py-1 rounded-full text-xs font-medium border max-w-[7rem] overflow-hidden text-ellipsis whitespace-nowrap bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
                {clerkName}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'invoiceNumber',
        header: 'INV #',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: (_appeal: Appeal) => (
          <span className="text-sm text-gray-900 dark:text-gray-100">{`${_appeal.invoiceNumber} ${_appeal?.splitInvoice?.ids?.length ? '(Split Invoice)' : ''}`}</span>
        ),
      },
      {
        key: 'invoiceStatus',
        header: 'INV Status',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: (_appeal: Appeal) => {
          const label = get(INVOICE_STATUS_LABELS, _appeal.invoiceStatus, '-');
          const className = get(INVOICE_STATUS_CLASSES, _appeal.invoiceStatus, '');
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${className}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        key: 'totalAmount',
        header: 'INV Amount',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: (_appeal: Appeal) => (
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">
              Invoice Amount
            </span>
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(_appeal.totalAmount)}
            </span>

            <div className="flex gap-2 text-xs mt-1 justify-between">
              <span className="text-gray-600 dark:text-gray-400">Due</span>
              <span className="text-green-600 font-medium">
                {numberUtil.formatCurrency(_appeal.amountDue)}
              </span>
            </div>

            <div className="flex gap-2 text-xs justify-between">
              <span className="text-gray-600 dark:text-gray-400">Paid</span>
              <span className="text-green-600 font-medium">
                {numberUtil.formatCurrency(_appeal.paid ?? 0)}
              </span>
            </div>

            <div className="flex gap-2 text-xs justify-between">
              <span className="text-gray-600 dark:text-gray-400">Credits</span>
              <span className="text-gray-900 dark:text-gray-100">
                {numberUtil.formatCurrency(_appeal.creditsIssued ?? 0)}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'amountDue',
        header: 'Outstanding AR',
        align: 'left',
        isSortable: true,
        isResizable: true,
        defaultWidth: 220,
        renderCell: (_appeal: Appeal) => {
          return (
            <div className="flex flex-col items-start min-w-[12rem]">
              <div className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 mt-1 w-full flex justify-between items-center">
                <span className="text-[10px] text-gray-900 dark:text-gray-100 uppercase tracking-wide block">
                  Client Refused to Pay
                </span>
                <span className="text-green-600 font-semibold text-sm">
                  {numberUtil.formatCurrency(_appeal.remainingAmount ?? 0)}
                </span>
              </div>

              <div className="flex flex-col text-xs mt-1 w-full">
                <div className="flex gap-1 justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Write-Off</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {numberUtil.formatCurrency(_appeal.approvedWriteOffAmount ?? 0)}
                  </span>
                </div>
                <div className="flex gap-1 justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Agreed to Pay</span>
                  <span className="text-gray-900 dark:text-gray-100 text">
                    {numberUtil.formatCurrency(_appeal.collectedAmount ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'deadline',
        header: 'Deadlines',
        align: 'left',
        isResizable: true,
        isSortable: true,
        defaultWidth: 180,
        renderCell: (_appeal: Appeal) => {
          const attyDeadline = get(_appeal, 'attyDeadline');
          const clientDeadline = get(_appeal, 'deadline');

          if (!attyDeadline && !clientDeadline) {
            return <span className="text-sm text-gray-900 dark:text-gray-100">-</span>;
          }

          return (
            <div className="flex flex-col items-start gap-1 min-w-[5rem]">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                  Atty Response
                </span>
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  {attyDeadline ? dateUtil.formatDate(attyDeadline, dateUtil.DateFormat.US) : '-'}
                </span>
              </div>
              <hr className="w-full border-t border-gray-300 dark:border-gray-600" />
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                  Client Appeal
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {clientDeadline
                    ? dateUtil.formatDate(clientDeadline, dateUtil.DateFormat.US)
                    : '-'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'billAnalysisReport',
        header: 'BAR',
        align: 'center',
        isResizable: true,
        renderCell: (_appeal: Appeal) => (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <BARColumn
              invoiceId={_appeal.invoiceId}
              key={`appeals-bar-column-${_appeal.invoiceId}`}
            />
          </button>
        ),
      },
      {
        key: 'status',
        header: 'Appeal Status',
        align: 'left',
        isResizable: false,
        defaultWidth: 150,
        renderCell: (_appeal: Appeal) => {
          return (
            <button
              type="button"
              className="appearance-none bg-transparent border-0 p-0 m-0 cursor-pointer block text-left"
              onClick={e => {
                e.stopPropagation();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                }
              }}
            >
              <AppealStatusBadge
                key={`cell-appeal-status-collected-${_appeal.invoiceId}`}
                invoiceId={_appeal.invoiceId}
                invoiceNumber={_appeal.invoiceNumber}
                params={isAppealSelected ? appealSelectedParams : tableParams}
                currentStatus={_appeal.status}
                basicInformation={{
                  matterDisplayNumber: _appeal.matterDisplayNumber || '-',
                  clientName: _appeal.clientName || '-',
                  invoiceNumber: _appeal.invoiceNumber || '-',
                  invoiceDate: _appeal.invoiceDate,
                  totalAmount: _appeal.totalAmount || 0,
                  amountDue: _appeal.amountDue || 0,
                  paid: _appeal.paid ?? 0,
                  creditsIssued: _appeal.creditsIssued ?? 0,
                  responsibleAttorneyName: _appeal.responsibleAttorneyName ?? '-',
                  remainingAmount: _appeal.remainingAmount ?? 0,
                }}
                onMutationStateChange={onMutationStateChange}
                isAppealSelected={isAppealSelected}
              />
            </button>
          );
        },
      },
      {
        key: 'actions',
        header: '#',
        align: 'center',
        isResizable: true,
        defaultWidth: 150,
        renderCell: (row: Appeal) => {
          const isMutating = mutatingRows.has(row.invoiceId);
          const showStatusActions = rollbackTargetsFor(row.status).length > 0;
          return (
            <div className="relative items-center justify-center">
              <Button
                variant="ghost"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActionRef(e.currentTarget);
                  setActionSelectedAppeal(row);
                }}
                className={cn(
                  actionSelectedAppeal?.invoiceId === row.invoiceId
                    ? 'bg-xtnd-light dark:bg-xtnd-dark '
                    : '',
                  'outline-none'
                )}
                title="More actions"
              >
                <Icon name="HiDotsVertical" className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </Button>

              <ActionsMenu
                isOpen={actionSelectedAppeal?.invoiceId === row.invoiceId}
                onClose={() => {
                  setActionSelectedAppeal(null);
                  setActionRef(null);
                }}
                ref={actionMenuRef}
                triggerElement={actionRef}
              >
                {includes(HIDE_ATTORNEY_RESPONSE_STATUS, row.status) ? null : (
                  <ActionsMenuItem
                    key={`attorney-response-${row.invoiceId}`}
                    icon="HiOutlineDocument"
                    iconColor="text-xtnd-blue"
                    label="Attorney Response"
                    onClick={e => {
                      e.stopPropagation();
                      handleViewAttorneyResponse(row);
                      setActionSelectedAppeal(null);
                      setActionRef(null);
                    }}
                  />
                )}
                {includes(HIDE_ATTORNEY_RESPONSE_STATUS, row.status) ? null : (
                  <ActionsMenuItem
                    key={`attorney-response-${row.invoiceId}`}
                    icon="HiClipboardCopy"
                    iconColor="text-xtnd-blue"
                    label="Attorney Response Link"
                    onClick={e => {
                      e.stopPropagation();
                      handleCopyAttorneyResponseLink(row);
                      setActionSelectedAppeal(null);
                      setActionRef(null);
                    }}
                  />
                )}
                <ActionsMenuItem
                  key={`activity-${row.invoiceId}`}
                  icon="HiClock"
                  iconColor="text-xtnd-blue"
                  label="Activity"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedActivityAppeal(row);
                    setIsActivityOpen(true);
                    setActionSelectedAppeal(null);
                    setActionRef(null);
                  }}
                />
                {canEdit(row.status, row.appealType) && handleEdit && (
                  <ActionsMenuItem
                    key={`edit-${row.invoiceId}`}
                    icon="HiPencil"
                    iconColor="text-amber-500"
                    label="Edit"
                    disabled={isMutating}
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit(row);
                      setActionSelectedAppeal(null);
                      setActionRef(null);
                    }}
                  />
                )}
                {showStatusActions && (
                  <ActionsMenuItem
                    key={`rollback-${row.invoiceId}`}
                    icon="HiRewind"
                    iconColor="text-amber-500"
                    label="Rollback"
                    disabled={isMutating}
                    onClick={e => {
                      e.stopPropagation();
                      handleRollback(row);
                      setActionSelectedAppeal(null);
                      setActionRef(null);
                    }}
                  />
                )}
                {[AppealStatus.APPEAL_DENIED, AppealStatus.DEADLINE_EXPIRED].includes(row.status) && handleWriteOffRemaining && (
                  <ActionsMenuItem
                    key={`write-off-remaining-${row.invoiceId}`}
                    icon="HiCurrencyDollar"
                    iconColor="text-red-600"
                    label="Write-Off Remaining Amount"
                    disabled={isMutating}
                    onClick={e => {
                      e.stopPropagation();
                      handleWriteOffRemaining(row);
                      setActionSelectedAppeal(null);
                      setActionRef(null);
                    }}
                  />
                )}
              </ActionsMenu>
            </div>
          );
        },
      },
    ];

    if (isEmpty(rowSelected)) {
      return [
        checkboxColumn as DataTableColumn<any>,
        clientNameColumn as DataTableColumn<any>,
        ...(baseColumns as DataTableColumn<any>[]),
      ];
    }

    // In grouped client/attorney detail view, include checkbox column first
    return [checkboxColumn as DataTableColumn<any>, ...(baseColumns as DataTableColumn<any>[])];
  }
  // --- CLIENT/ATTORNEY GROUPING COLUMNS (Grouped View) ---
  const isClientGroup = groupBy === GroupByOption.CLIENT;
  const nameKey = 'groupName';
  const nameValue = (row: AppealGroup) => row.groupName || '';

  return [
    {
      key: nameKey,
      header: isClientGroup ? 'Client' : 'Responsible Attorney',
      align: 'left',
      isResizable: true,
      defaultWidth: 200,
      renderCell: (row: AppealGroup) => (
        <Tooltip
          content={nameValue(row) || ''}
          position="auto"
          className="w-full rounded-md"
          contentClassName="min-w-[12rem] h-full"
        >
          <span className="text-sm text-gray-900 dark:text-gray-100 max-w-[12rem] block">
            {nameValue(row) || ''}
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'totalInvoices',
      header: 'Invoices',
      align: 'right',
      isResizable: true,
      defaultWidth: 150,
      renderCell: (row: AppealGroup) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">{row.totalInvoices}</span>
      ),
    },

    {
      key: 'totalAmount',
      header: 'Total Amount',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (_appeal: AppealGroup) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {numberUtil.formatCurrency(_appeal.totalAmount)}
        </span>
      ),
    },
    {
      key: 'amountPaid',
      header: 'Amount Paid',
      align: 'left',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (_appeal: AppealGroup) => (
        <div className="flex flex-col min-w-[8rem]">
          <div className="flex justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Paid</span>
            <span className="text-green-600 font-medium">
              {numberUtil.formatCurrency(_appeal.paid ?? 0)}
            </span>
          </div>

          <div className="flex justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Credits Issued</span>
            <span className="text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(_appeal.creditsIssued ?? 0)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'amountDue',
      header: 'Amount Due',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (_appeal: AppealGroup) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {numberUtil.formatCurrency(_appeal.amountDue)}
        </span>
      ),
    },
    {
      key: 'maxDaysPastDue',
      header: 'DPD',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (_appeal: AppealGroup) => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div
            className={`text-sm ${_appeal.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}
          >
            {_appeal.maxDaysPastDue}
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: '',
      align: 'center',
      renderCell: (_appeal: AppealGroup) => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <button>
            <Icon name="FaAngleRight" size="sm" className="opacity-100" />
          </button>
        </div>
      ),
    },
  ];
}
